import { Logo } from "./Logo";
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Globe, Menu, User } from "lucide-react";
import { useLanguage } from "../i18n";
import { useAuth } from "../auth";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { LightDimmer, ClickBulb } from "./LightDimmer";

export const Header = () => {
  const { lang, setLang, t } = useLanguage();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const { pathname } = useLocation();
  const links = [
    { href: "/chi-siamo", label: t.nav.about, id: "nav-about" },
    { href: "/metodo", label: t.nav.method, id: "nav-method" },
    { href: "/servizi", label: t.nav.pricing, id: "nav-pricing" },
    { href: "/progetti", label: t.nav.projects, id: "nav-projects" },
    { href: "/brand-cataloghi", label: t.nav.brands, id: "nav-brands" },
    { href: "/contatti-supporto", label: t.nav.contactSupport, id: "nav-contact-support" },
  ];
  const active = (href) => (href === "/" ? pathname === "/" : pathname.startsWith(href));
  // Inline links only where the whole bar fits (Spanish and German labels are longer); narrower screens use the menu button.
  const navShow = { es: "min-[1400px]:flex", de: "min-[1360px]:flex" }[lang] || "min-[1280px]:flex";
  const navHide = { es: "min-[1400px]:hidden", de: "min-[1360px]:hidden" }[lang] || "min-[1280px]:hidden";

  const LangSelector = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="language-selector"
          className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-inksoft hover:text-ink transition-colors"
          aria-label="Language"
        >
          <Globe className="w-4 h-4" strokeWidth={1.5} />
          {lang.toUpperCase()}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white border border-ink/10 rounded-sm shadow-lg">
        <DropdownMenuItem data-testid="language-option-it" onClick={() => setLang("it")} className="text-sm cursor-pointer">
          Italiano
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="language-option-en" onClick={() => setLang("en")} className="text-sm cursor-pointer">
          English
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="language-option-fr" onClick={() => setLang("fr")} className="text-sm cursor-pointer">
          Français
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="language-option-de" onClick={() => setLang("de")} className="text-sm cursor-pointer">
          Deutsch
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="language-option-es" onClick={() => setLang("es")} className="text-sm cursor-pointer">
          Español
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <header className={`fixed top-0 inset-x-0 z-50 backdrop-blur-lg backdrop-saturate-150 border-b transition-[background-color,border-color,box-shadow] duration-500 ${scrolled ? "bg-white/60 border-ink/10 shadow-[0_6px_24px_-14px_rgba(42,39,38,0.35)]" : "bg-white/40 border-transparent"}`}>
      <ClickBulb />
      <div className="max-w-[1760px] mx-auto px-4 sm:px-6 lg:px-8 min-[1536px]:px-12 h-14 flex items-center justify-between gap-4 min-[1536px]:gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center shrink-0 text-ink" aria-label="FArLight">
          <Logo className="h-8 w-auto" accent="#B8875A" />
        </Link>
        <nav className={`hidden ${navShow} items-center gap-3 min-[1536px]:gap-4`}>
          {links.map((l) => (
            <Link key={l.id} data-testid={l.id} to={l.href} className={`whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.1em] transition-colors ${active(l.href) ? "text-amberdark" : "text-inksoft hover:text-ink"}`}>
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-2.5 sm:gap-3 xl:gap-4 shrink-0">
          <LangSelector />
          <LightDimmer />
          {user ? (
            <Link
              to={user.is_admin ? "/admin" : "/account"}
              data-testid="account-link"
              className="flex items-center gap-2 whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.12em] text-inksoft hover:text-ink transition-colors"
            >
              <User className="w-4 h-4" strokeWidth={1.5} />
              <span className="hidden sm:inline">{user.is_admin ? t.nav.admin : user.name?.split(" ")[0]}</span>
            </Link>
          ) : (
            <Link
              to="/login"
              data-testid="login-link"
              aria-label={t.nav.reserved}
              className="flex items-center gap-2 whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.12em] text-inksoft hover:text-ink transition-colors"
            >
              <User className="w-4 h-4" strokeWidth={1.5} />
              {/* Long labels (e.g. German) leave no room for the menu button on phones */}
              <span className={t.nav.reserved.length > 16 ? "hidden sm:inline" : undefined}>{t.nav.reserved}</span>
            </Link>
          )}
          <Link data-testid="header-cta-advanced" to="/richiedi-concept?plan=advanced" className="hidden min-[1536px]:inline-flex items-center h-10 px-4 border border-ink/25 hover:border-[#C5A059] hover:text-[#C5A059] rounded-sm text-[11px] font-semibold uppercase tracking-[0.15em] text-ink transition-colors">{t.twin.navAdvanced}</Link>
          <Button
            asChild
            data-testid="header-cta-button"
            className="hidden sm:inline-flex bg-ink text-cream hover:bg-espresso rounded-sm text-[11px] font-semibold uppercase tracking-[0.12em] px-5 h-9 whitespace-nowrap"
          >
            <Link to="/richiedi-concept?plan=base">{t.nav.cta}</Link>
          </Button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-button" className={`${navHide} text-ink`} aria-label="Menu">
                <Menu className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-cream border-l border-ink/10 w-72">
              <nav className="flex flex-col gap-6 mt-10">
                {links.map((l) => (
                  <Link key={l.id} data-testid={`mobile-${l.id}`} to={l.href} onClick={() => setOpen(false)} className={`font-display text-2xl transition-colors ${active(l.href) ? "text-amberdark" : "text-ink hover:text-amberdark"}`}>
                    {l.label}
                  </Link>
                ))}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
